import { definitions, paths } from '@luxuryescapes/contract-svc-lux-loyalty'

type ServerLuxLoyaltyAccount = definitions['accountSummary']
type ServerLuxLoyaltyPendingPoints = definitions['pendingPoints']
type ServerLoyaltyActivity = paths['/api/lux-loyalty/account/activities']['get']['responses']['200']['content']['application/json']['result']['activities'][0]

const disabledBenefitCodes = new Set<App.LuxLoyaltyBenefitType>(['hidden_access'])

export function luxLoyaltyAccountMapper(response: ServerLuxLoyaltyAccount | null): App.LuxLoyaltyAccount | undefined {
  if (!response) return undefined
  const enabledBenefits = response.benefits.filter(benefit => !disabledBenefitCodes.has(benefit.code))

  return {
    accountNumber: response.accountNumber,
    points: response.points,
    tier: response.tier,
    statusCredits: response.statusCredits,
    ibsMemberId: response.ibsMemberId,
    statusReviewDate: response.statusReviewDate ?? undefined,
    totalCreditsForTierRetention: response.totalCreditsForTierRetention ?? 0,
    totalCreditsForTierUpgrade: response.totalCreditsForTierUpgrade ?? 0,
    numberOfNights: response.numberOfNights ?? 0,
    numberOfNightsForTierUpgrade: response.numberOfNightsForTierUpgrade ?? 0,
    numberOfNightsForTierRetention: response.numberOfNightsForTierRetention ?? 0,
    benefits: enabledBenefits.map((benefit): App.LuxLoyaltyBenefit => ({
      code: benefit.code,
      description: benefit.description,
      remaining: benefit.details.remaining === 'unlimited' ? undefined : benefit.details.remaining,
      limit: benefit.details.limit === 'unlimited' ? undefined : benefit.details.limit,
      unlimited: benefit.details.remaining === 'unlimited' || benefit.details.limit === 'unlimited',
      value: benefit.details.value ? {
        amount: benefit.details.value.amount,
        unit: benefit.details.value.unit,
        currency: benefit.details.value.currency,
      } : undefined,
    })),
  }
}

export function luxLoyaltyPendingPointsMapper(pendingPoints: Array<ServerLuxLoyaltyPendingPoints>): Array<App.OrderLoyaltyPendingPoints> {
  return pendingPoints.map((pendingPoint) => ({
    itemId: pendingPoint.itemId,
    points: pendingPoint.points,
    statusCredits: pendingPoint.statusCredits,
  }))
}

export function luxLoyaltyAccountPendingPointsMapper(pendingPoints: Array<ServerLuxLoyaltyPendingPoints>): Array<App.OrderLoyaltyPendingPoints> {
  return pendingPoints.map((pendingPoint) => ({
    id: pendingPoint.id,
    itemId: pendingPoint.itemId,
    orderId: pendingPoint.orderId,
    points: pendingPoint.points,
    statusCredits: pendingPoint.statusCredits,
    createdAt: pendingPoint.createdAt,
  }))
}

function getLocalActivityType(activity: ServerLoyaltyActivity): App.LuxLoyaltyAccountActivityType {
  switch (activity.activityType) {
    // case 'Accrual Activity':

    case 'Cancellation Activity':
      return 'cancellation'
    case 'Expire Points':
      return 'expired_points'
    case 'Expired Points Reversal':
      return 'expired_points_reversal'
    case 'Manual Adjustment':
      return 'manual'
    case 'Member Enrolment Activity':
      return 'enrolment'
    case 'Accrual Activity':
      if (activity.basePoints) {
        return 'earn_points'
      } else if (activity.statusCredits) {
        return 'earn_status_credits'
      }
      // not sure what else could accrue
      return 'other'
    case 'Redemption':
      if (activity.basePoints) {
        return activity.basePoints > 0 ? 'earn_points' : 'spend_points'
      } else if (activity.statusCredits) {
        return 'earn_status_credits'
      } if (activity.itemId) {
        return 'benefit_redemption'
      }
      return 'other'
    case 'Tier Change Activity':
      return 'tier_change'
    default:
      return 'other'
  }
}

export function luxLoyaltyAccountActivityMapper(activities: Array<ServerLoyaltyActivity>): Array<App.LuxLoyaltyAccountActivity> {
  return activities.map((activity): App.LuxLoyaltyAccountActivity => ({
    id: activity.id,
    date: activity.activityDate,
    type: getLocalActivityType(activity),
    description: activity.activityDescription,
    basePoints: activity.basePoints ?? 0,
    orderId: activity.orderId,
    orderItemId: activity.itemId,
    statusCredits: activity.statusCredits ?? 0,
  }))
}
